import { create } from 'zustand';
import type { User as FirebaseUser } from 'firebase/auth';
import { doc, onSnapshot, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import {
  login as fbLogin,
  signup as fbSignup,
  googleLogin as fbGoogleLogin,
  appleLogin as fbAppleLogin,
  logout as fbLogout,
  onAuthChange,
  sendVerificationEmail,
  reloadUser,
} from '../lib/firebase/auth';
import { setDoc, updateDoc, serverTimestamp } from '../lib/firebase/firestore';
import type { User } from '../types/user';
import { useConfigStore, waitForConfig } from './configStore';
import {
  generateSessionId,
  getCurrentSessionId,
  clearSessionId,
  getDeviceInfo,
  getDetailedDeviceInfo,
  getNetworkInfo,
} from '../utils/session';

interface SessionConflictInfo {
  device: string;
  /** credentials to retry after user confirms */
  email: string;
  password: string;
  loginType: 'email' | 'google';
}

interface AuthState {
  /** Firebase auth user */
  firebaseUser: FirebaseUser | null;
  /** Firestore user profile */
  user: User | null;
  /** Auth state has been resolved at least once */
  initialized: boolean;
  loading: boolean;

  /** Session conflict detected — waiting for user decision */
  sessionConflict: SessionConflictInfo | null;
  /** True while login is checking for session conflicts (blocks AuthLayout redirect) */
  sessionChecking: boolean;
  /** Force-logged-out by another device */
  forcedLogout: boolean;

  /** Email verification pending after signup */
  pendingEmailVerification: boolean;

  // Actions
  init: () => () => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  googleSignIn: (isSignup?: boolean) => Promise<void>;
  appleSignIn: (isSignup?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;

  /** Check if email is verified (reload firebase user) */
  checkEmailVerified: () => Promise<boolean>;
  /** Resend verification email */
  resendVerificationEmail: () => Promise<void>;
  /** Clear pending email verification state */
  clearPendingEmailVerification: () => void;

  /** User confirmed: override the other session */
  resolveSessionConflict: () => Promise<void>;
  /** User cancelled: sign out and stay on login page */
  cancelSessionConflict: () => Promise<void>;
  /** Dismiss the forced-logout alert */
  clearForcedLogout: () => void;
}

let profileUnsub: (() => void) | null = null;
/** True after we've written our session ID to Firestore — only then check for takeover */
let sessionEstablished = false;
/** True while writing a new session — skip force-logout during this window */
let sessionWriting = false;

async function writeSession(uid: string): Promise<string> {
  sessionWriting = true;
  const sessionId = generateSessionId();
  const [deviceInfo, networkInfo] = await Promise.all([
    getDetailedDeviceInfo().catch(() => ({})),
    getNetworkInfo().catch(() => ({})),
  ]);

  // Apple Guideline 5.1.2: do NOT write lat/lng on login. The user must explicitly opt in
  // (per-session) to have their location shown on the map. Location sharing is handled by
  // updateMyLocation() only when shareLocationOnMap === true.
  const { useLocationStore } = await import('./locationStore');
  const { deleteField } = await import('firebase/firestore');
  // Reset per-session share flags so the user is re-prompted after login.
  useLocationStore.getState().resetShareLocationOnMap();

  await updateDoc(doc(db, 'users', uid), {
    activeSessionId: sessionId,
    activeSessionDevice: getDeviceInfo(),
    activeSessionAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
    appVersion: __APP_VERSION__,
    lastDeviceInfo: { ...deviceInfo, lastUpdated: serverTimestamp() },
    lastNetworkInfo: { ...networkInfo, lastUpdated: serverTimestamp() },
    // Clear any stale location from a previous session so the user is hidden from the map
    // until they explicitly opt in via the post-login MapLocationConsentModal.
    lat: deleteField(),
    lng: deleteField(),
    // Clear previous device's push token — the new device will re-register its own
    pushToken: '',
  });
  sessionEstablished = true;
  sessionWriting = false;
  return sessionId;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  firebaseUser: null,
  user: null,
  initialized: false,
  loading: true,
  sessionConflict: null,
  sessionChecking: false,
  forcedLogout: false,
  pendingEmailVerification: false,

  init: () => {
    // Fallback: if onAuthStateChanged never fires (e.g. network issue), force initialized after 5s
    const fallbackTimer = setTimeout(() => {
      if (!get().initialized) {
        console.warn('[Auth] onAuthStateChanged timeout — forcing initialized');
        set({ initialized: true, loading: false });
      }
    }, 5000);

    const unsubAuth = onAuthChange((fbUser) => {
      clearTimeout(fallbackTimer);
      set({ firebaseUser: fbUser, initialized: true, loading: false });

      // Cleanup previous profile listener
      profileUnsub?.();
      profileUnsub = null;

      if (fbUser) {
        // Listen to user profile document
        profileUnsub = onSnapshot(
          doc(db, 'users', fbUser.uid),
          (snap) => {
            if (snap.exists()) {
              const userData = { uid: snap.id, ...snap.data() } as User;
              set({ user: userData });

              const mySessionId = getCurrentSessionId();

              // Auto-login session validation on app restart
              // Skip during explicit login or while session conflict dialog is showing
              if (!sessionEstablished && !sessionWriting && !get().sessionChecking && !get().sessionConflict && userData.activeSessionId) {
                if (mySessionId && userData.activeSessionId === mySessionId) {
                  // Our session is still valid — restore sessionEstablished flag
                  sessionEstablished = true;
                  // Update device/network info on auto-login (writeSession only runs on explicit login)
                  Promise.all([
                    getDetailedDeviceInfo().catch(() => ({})),
                    getNetworkInfo().catch(() => ({})),
                  ]).then(([devInfo, netInfo]) => {
                    updateDoc(doc(db, 'users', fbUser.uid), {
                      appVersion: __APP_VERSION__,
                      lastDeviceInfo: { ...devInfo, lastUpdated: serverTimestamp() },
                      lastNetworkInfo: { ...netInfo, lastUpdated: serverTimestamp() },
                    }).catch(() => {});
                  });
                } else {
                  // Either no local session (logged out / fresh install) or another device took over
                  // Force logout — user must explicitly login again (which triggers conflict check)
                  console.warn('[Auth] Session invalid on app restart —', mySessionId ? 'another device is active' : 'no local session');
                  set({ forcedLogout: true });
                  clearSessionId();
                  localStorage.removeItem('ourhertz_pin_hash');
                  localStorage.removeItem('ourhertz_pin_enabled');
                  fbLogout().catch(() => {});
                  import('../services/pushService')
                    .then(({ resetPushState }) => resetPushState())
                    .catch(() => {});
                  set({ user: null, firebaseUser: null });
                  return;
                }
              }

              // Force-logout detection: if session was established and activeSessionId changed
              // Skip during session write (resolving conflict or establishing new session)
              if (sessionEstablished && !sessionWriting) {
                if (
                  userData.activeSessionId &&
                  mySessionId &&
                  userData.activeSessionId !== mySessionId
                ) {
                  console.warn('[Auth] Session taken over by another device');
                  sessionEstablished = false;
                  set({ forcedLogout: true });
                  // Do NOT clear pushToken here — the new device already owns it
                  clearSessionId();
                  localStorage.removeItem('ourhertz_pin_hash');
                  localStorage.removeItem('ourhertz_pin_enabled');
                  fbLogout().catch(() => {});
                  import('../services/pushService')
                    .then(({ resetPushState }) => resetPushState())
                    .catch(() => {});
                  set({ user: null, firebaseUser: null });
                  return;
                }
              }

              // Sync user settings to locationStore (lazy import to avoid circular dep)
              import('./locationStore').then(({ useLocationStore }) => {
                const locStore = useLocationStore.getState();
                // Load per-user settings from localStorage (radarRadius, receptionMode, etc.)
                locStore.loadUserSettings(userData.uid);
                if (userData.syncIntervalSec && userData.syncIntervalSec !== locStore.syncIntervalSec) {
                  locStore.setSyncInterval(userData.syncIntervalSec);
                }
              });
            }
          },
          (err) => {
            if (err.code !== 'permission-denied') {
              console.error('[AuthStore] Profile listener error:', err);
            }
          },
        );
      } else {
        set({ user: null });
      }
    });

    return () => {
      unsubAuth();
      profileUnsub?.();
      profileUnsub = null;
    };
  },

  login: async (email, password) => {
    set({ loading: true, sessionChecking: true });
    try {
      const cred = await fbLogin(email, password);
      const uid = cred.user.uid;

      // Check for active session on another device
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists()) {
        const data = snap.data();
        if (data.activeSessionId) {
          const mySessionId = getCurrentSessionId();
          if (mySessionId !== data.activeSessionId) {
            // Session conflict — keep Firebase auth alive, ask user
            set({
              loading: false,
              sessionChecking: false,
              sessionConflict: {
                device: data.activeSessionDevice || '',
                email,
                password,
                loginType: 'email',
              },
            });
            return;
          }
        }
      }

      // No conflict — establish session
      await writeSession(uid);
    } finally {
      set({ loading: false, sessionChecking: false });
    }
  },

  signup: async (email, password) => {
    set({ loading: true });
    try {
      // Ensure config is loaded before signup
      const cfg = await waitForConfig();
      const cooldownDays = cfg.deletionCooldownDays ?? 7;
      const { checkReregistrationAllowed } = await import('../services/accountService');
      const check = await checkReregistrationAllowed(email, cooldownDays);
      if (!check.allowed) {
        throw new Error(`COOLDOWN:${check.daysRemaining}`);
      }

      const cred = await fbSignup(email, password);
      const uid = cred.user.uid;

      // Send email verification
      await sendVerificationEmail(cred.user);

      const defaults = cfg.newUserDefaults ?? {};
      const signupCells = cfg.signupDefaultCells ?? 1_000_000;
      await setDoc(doc(db, 'users', uid), {
        uid,
        email,
        nickname: email.split('@')[0],
        gender: 'unknown',
        signupProvider: 'email',
        isGuest: false,
        isAdmin: false,
        isOperator: false,
        isBot: false,
        battery: signupCells,
        manualFreqApplied: false,
        radius: defaults.radarRadius ?? defaults.radius ?? 5,
        messageReceptionRadius: defaults.messageReceptionRadius ?? 100,
        rejectBotMessages: defaults.rejectBotMessages ?? false,
        pushNotificationsEnabled: defaults.pushNotificationsEnabled ?? true,
        genderFilter: defaults.genderFilter ?? 'all',
        activeThresholdMinutes: defaults.activeThresholdMinutes ?? 60,
        syncIntervalSec: defaults.syncIntervalSec ?? 10,
        locationJumpRange: defaults.locationJumpRange ?? { min: 100, max: 150 },
        connectionNotificationEnabled: defaults.connectionNotificationEnabled ?? true,
        connectionNotificationDistanceThreshold: defaults.connectionNotificationDistanceThreshold ?? 1000,
        messageHistoryHours: defaults.messageHistoryHours ?? 6,
        ...(defaults.pushSettings ? { pushSettings: defaults.pushSettings } : {}),
        emailNotifications: { onComment: true, onReportReply: true, onDmReceived: true },
        blockedUserIds: [],
        profileSetupDone: false,
        activeSessionId: generateSessionId(),
        activeSessionDevice: getDeviceInfo(),
        activeSessionAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
      });
      // Record signup bonus in batteryHistory
      addDoc(collection(db, 'users', uid, 'batteryHistory'), {
        type: 'SIGNUP_BONUS', amount: signupCells, timestamp: Date.now(), balance: signupCells,
      }).catch(() => {});
      sessionEstablished = true;
      set({ pendingEmailVerification: true });
    } finally {
      set({ loading: false });
    }
  },

  googleSignIn: async (isSignup = false) => {
    set({ loading: true, sessionChecking: true });
    let cred: Awaited<ReturnType<typeof fbGoogleLogin>>;
    try {
      try {
        cred = await fbGoogleLogin();
      } catch (err: any) {
        if (err?.code === 'auth/account-exists-with-different-credential') {
          const existingEmail = err?.customData?.email ?? err?.email ?? '';
          const e: any = new Error(`EMAIL_EXISTS_DIFFERENT_PROVIDER:${existingEmail}`);
          throw e;
        }
        throw err;
      }
      const uid = cred.user.uid;
      const email = cred.user.email ?? '';
      const displayName = cred.user.displayName ?? email.split('@')[0];
      const photoURL = cred.user.photoURL ?? '';

      // Check if user profile already exists
      const snap = await getDoc(doc(db, 'users', uid));

      if (snap.exists()) {
        // Existing user — check session conflict
        const data = snap.data();
        if (data.activeSessionId) {
          const mySessionId = getCurrentSessionId();
          if (mySessionId !== data.activeSessionId) {
            set({
              loading: false,
              sessionChecking: false,
              sessionConflict: {
                device: data.activeSessionDevice || '',
                email: '',
                password: '',
                loginType: 'google',
              },
            });
            return;
          }
        }
        // No conflict — establish session
        await writeSession(uid);
      } else if (!isSignup) {
        // Login page: reject new users — they must sign up first
        // Delete the auto-created Firebase Auth account
        try {
          await cred.user.delete();
        } catch {
          // Client-side delete failed — use Admin SDK via Cloud Function
          try {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const { app } = await import('../lib/firebase/app');
            const fn = httpsCallable(getFunctions(app), 'deleteAuthAccount');
            await fn({ uid });
          } catch {}
        }
        await fbLogout();
        set({ firebaseUser: null });
        throw new Error('NOT_REGISTERED');
      } else {
        // New Google user — ensure config is loaded
        const gglCfg = await waitForConfig();
        const cooldownDays = gglCfg.deletionCooldownDays ?? 7;
        const { checkReregistrationAllowed } = await import('../services/accountService');
        const check = await checkReregistrationAllowed(email, cooldownDays);
        if (!check.allowed) {
          await fbLogout();
          set({ firebaseUser: null });
          throw new Error(`COOLDOWN:${check.daysRemaining}`);
        }

        // Create new profile for Google user
        const gglDefaults = gglCfg.newUserDefaults ?? {};
        const gglSignupCells = gglCfg.signupDefaultCells ?? 1_000_000;
        await setDoc(doc(db, 'users', uid), {
          uid,
          email,
          nickname: displayName,
          gender: 'unknown',
          profileImageUrl: photoURL,
          signupProvider: 'google',
          isGuest: false,
          isAdmin: false,
          isOperator: false,
          isBot: false,
          battery: gglSignupCells,
          manualFreqApplied: false,
          radius: gglDefaults.radarRadius ?? gglDefaults.radius ?? 5,
          messageReceptionRadius: gglDefaults.messageReceptionRadius ?? 100,
          rejectBotMessages: gglDefaults.rejectBotMessages ?? false,
          pushNotificationsEnabled: gglDefaults.pushNotificationsEnabled ?? true,
          genderFilter: gglDefaults.genderFilter ?? 'all',
          activeThresholdMinutes: gglDefaults.activeThresholdMinutes ?? 60,
          syncIntervalSec: gglDefaults.syncIntervalSec ?? 10,
          locationJumpRange: gglDefaults.locationJumpRange ?? { min: 100, max: 150 },
          connectionNotificationEnabled: gglDefaults.connectionNotificationEnabled ?? true,
          connectionNotificationDistanceThreshold: gglDefaults.connectionNotificationDistanceThreshold ?? 1000,
          messageHistoryHours: gglDefaults.messageHistoryHours ?? 6,
          ...(gglDefaults.pushSettings ? { pushSettings: gglDefaults.pushSettings } : {}),
          emailNotifications: { onComment: true, onReportReply: true, onDmReceived: true },
          blockedUserIds: [],
          profileSetupDone: false,
          activeSessionId: generateSessionId(),
          activeSessionDevice: getDeviceInfo(),
          activeSessionAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastActiveAt: serverTimestamp(),
        });
        // Record signup bonus in batteryHistory
        addDoc(collection(db, 'users', uid, 'batteryHistory'), {
          type: 'SIGNUP_BONUS', amount: gglSignupCells, timestamp: Date.now(), balance: gglSignupCells,
        }).catch(() => {});
        sessionEstablished = true;
      }
    } finally {
      set({ loading: false, sessionChecking: false });
    }
  },

  appleSignIn: async (isSignup = false) => {
    set({ loading: true, sessionChecking: true });
    let cred: Awaited<ReturnType<typeof fbAppleLogin>>;
    try {
      try {
        cred = await fbAppleLogin();
      } catch (err: any) {
        if (err?.code === 'auth/account-exists-with-different-credential') {
          const existingEmail = err?.email ?? err?.customData?.email ?? '';
          const e: any = new Error(`EMAIL_EXISTS_DIFFERENT_PROVIDER:${existingEmail}`);
          throw e;
        }
        throw err;
      }
      const uid = cred.user.uid;
      const email = cred.user.email ?? '';
      const displayName = cred.user.displayName ?? (email.split('@')[0] || 'Apple User');

      const snap = await getDoc(doc(db, 'users', uid));

      if (snap.exists()) {
        const data = snap.data();
        if (data.activeSessionId) {
          const mySessionId = getCurrentSessionId();
          if (mySessionId !== data.activeSessionId) {
            set({
              loading: false,
              sessionChecking: false,
              sessionConflict: {
                device: data.activeSessionDevice || '',
                email: '',
                password: '',
                loginType: 'google', // reuse same conflict flow
              },
            });
            return;
          }
        }
        await writeSession(uid);
      } else if (!isSignup) {
        try {
          await cred.user.delete();
        } catch {
          try {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const { app } = await import('../lib/firebase/app');
            const fn = httpsCallable(getFunctions(app), 'deleteAuthAccount');
            await fn({ uid });
          } catch {}
        }
        await fbLogout();
        set({ firebaseUser: null });
        throw new Error('NOT_REGISTERED');
      } else {
        const appleCfg = await waitForConfig();
        const cooldownDays = appleCfg.deletionCooldownDays ?? 7;
        const { checkReregistrationAllowed } = await import('../services/accountService');
        const check = await checkReregistrationAllowed(email, cooldownDays);
        if (!check.allowed) {
          await fbLogout();
          set({ firebaseUser: null });
          throw new Error(`COOLDOWN:${check.daysRemaining}`);
        }

        const appleDefaults = appleCfg.newUserDefaults ?? {};
        const appleSignupCells = appleCfg.signupDefaultCells ?? 1_000_000;
        await setDoc(doc(db, 'users', uid), {
          uid,
          email,
          nickname: displayName,
          gender: 'unknown',
          signupProvider: 'apple',
          isGuest: false,
          isAdmin: false,
          isOperator: false,
          isBot: false,
          battery: appleSignupCells,
          manualFreqApplied: false,
          radius: appleDefaults.radarRadius ?? appleDefaults.radius ?? 5,
          messageReceptionRadius: appleDefaults.messageReceptionRadius ?? 100,
          rejectBotMessages: appleDefaults.rejectBotMessages ?? false,
          pushNotificationsEnabled: appleDefaults.pushNotificationsEnabled ?? true,
          genderFilter: appleDefaults.genderFilter ?? 'all',
          activeThresholdMinutes: appleDefaults.activeThresholdMinutes ?? 60,
          syncIntervalSec: appleDefaults.syncIntervalSec ?? 10,
          locationJumpRange: appleDefaults.locationJumpRange ?? { min: 100, max: 150 },
          connectionNotificationEnabled: appleDefaults.connectionNotificationEnabled ?? true,
          connectionNotificationDistanceThreshold: appleDefaults.connectionNotificationDistanceThreshold ?? 1000,
          messageHistoryHours: appleDefaults.messageHistoryHours ?? 6,
          ...(appleDefaults.pushSettings ? { pushSettings: appleDefaults.pushSettings } : {}),
          emailNotifications: { onComment: true, onReportReply: true, onDmReceived: true },
          blockedUserIds: [],
          profileSetupDone: false,
          activeSessionId: generateSessionId(),
          activeSessionDevice: getDeviceInfo(),
          activeSessionAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastActiveAt: serverTimestamp(),
        });
        addDoc(collection(db, 'users', uid, 'batteryHistory'), {
          type: 'SIGNUP_BONUS', amount: appleSignupCells, timestamp: Date.now(), balance: appleSignupCells,
        }).catch(() => {});
        sessionEstablished = true;
      }
    } finally {
      set({ loading: false, sessionChecking: false });
    }
  },

  logout: async () => {
    const uid = get().user?.uid;

    // 1. 로컬 상태 즉시 리셋 (Firestore 응답 대기 없이)
    sessionEstablished = false;
    clearSessionId();
    set({ user: null, firebaseUser: null });
    try { sessionStorage.removeItem('ourhertz_last_notif_loc'); } catch {}
    import('../services/pushService').then(({ resetPushState }) => resetPushState()).catch(() => {});
    import('./locationStore').then(({ useLocationStore }) => {
      useLocationStore.getState().resetUserSettings();
      useLocationStore.getState().resetShareLocationOnMap();
    }).catch(() => {});

    // 2. Firestore 세션 정리 + 지도에서 내 위치 제거 (Apple 5.1.2)
    if (uid) {
      const { deleteField } = await import('firebase/firestore');
      updateDoc(doc(db, 'users', uid), {
        pushToken: '',
        activeSessionId: '',
        activeSessionDevice: '',
        isOnline: false,
        lat: deleteField(),
        lng: deleteField(),
      }).catch(() => {});
    }

    // 3. Firebase 로그아웃 (로컬 auth 토큰 제거)
    await fbLogout().catch(() => {});
  },

  resolveSessionConflict: async () => {
    const conflict = get().sessionConflict;
    if (!conflict) return;

    set({ loading: true, sessionConflict: null });
    try {
      const fbUser = get().firebaseUser;
      if (!fbUser) throw new Error('Not authenticated');
      await writeSession(fbUser.uid);
      // Re-register push token for the current device
      import('../services/pushService').then(({ resetPushState }) => {
        resetPushState(); // Allow re-init
      }).catch(() => {});
    } finally {
      set({ loading: false });
    }
  },

  cancelSessionConflict: async () => {
    set({ sessionConflict: null });
    clearSessionId();
    await fbLogout();
    set({ user: null, firebaseUser: null });
  },

  clearForcedLogout: () => {
    set({ forcedLogout: false });
  },

  checkEmailVerified: async () => {
    const fbUser = get().firebaseUser;
    if (!fbUser) return false;
    const verified = await reloadUser(fbUser);
    if (verified) {
      set({ pendingEmailVerification: false, firebaseUser: { ...fbUser } as any });
    }
    return verified;
  },

  resendVerificationEmail: async () => {
    // Use firebaseUser from store, or fall back to auth.currentUser (after page refresh)
    await sendVerificationEmail(get().firebaseUser);
  },

  clearPendingEmailVerification: () => {
    set({ pendingEmailVerification: false });
  },

  setUser: (user) => set({ user }),
}));
